import React, { Suspense } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import BaseLayout from '@/components/layout/BaseLayout';
import { useAuth } from '@/contexts/AuthContext';
import { lazyWithRetry } from '@/utils/lazyWithRetry';

// Lazy load all page components with automatic retry on chunk load failure
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard/Dashboard'));
const UserManagement = lazyWithRetry(() => import('./pages/UserManagement/UserManagement').then(m => ({ default: m.UserManagement })));
// const DeviceRegistry = lazyWithRetry(() => import('./pages/DeviceRegistry/DeviceRegistry').then(m => ({ default: m.DeviceRegistry })));
const Profile = lazyWithRetry(() => import('./pages/Profile/Profile').then(m => ({ default: m.Profile })));
const UserAccessManagement = lazyWithRetry(() => import('@/pages/admin/UserAccessManagement'));
// const BiogasManagement = lazyWithRetry(() => import('./pages/BiogasManagement/BiogasManagement'));
import {
  LayoutDashboard,
  Users,
  HardDrive,
  Shield,
  Zap
} from 'lucide-react';

// Loading component for lazy-loaded routes
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="text-center space-y-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p className="text-gray-600">Loading module...</p>
    </div>
  </div>
);

// Error Boundary Component
class ModuleErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Module Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="text-center space-y-4">
            <h2 className="text-2xl font-bold text-red-600">Module Error</h2>
            <p className="text-gray-600">Failed to load this module</p>
            <p className="text-sm text-gray-500">{this.state.error?.message}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const AdminModule: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const navigationItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <LayoutDashboard className="w-5 h-5" />,
      onClick: () => navigate('/admin'),
      isActive: location.pathname === '/admin' || location.pathname === '/admin/'
    },
    {
      id: 'users',
      label: 'User Management',
      icon: <Users className="w-5 h-5" />,
      onClick: () => navigate('/admin/users'),
      isActive: location.pathname === '/admin/users'
    },
    // {
    //   id: 'devices',
    //   label: 'Device Registry',
    //   icon: <HardDrive className="w-5 h-5" />,
    //   onClick: () => navigate('/admin/devices'),
    //   isActive: location.pathname === '/admin/devices'
    // },
    {
      id: 'access-management',
      label: 'Access Management',
      icon: <Shield className="w-5 h-5" />,
      onClick: () => navigate('/admin/access-management'),
      isActive: location.pathname === '/admin/access-management'
    },
    // {
    //   id: 'biogas',
    //   label: 'Biogas Management',
    //   icon: <Zap className="w-5 h-5" />,
    //   onClick: () => navigate('/admin/biogas'),
    //   isActive: location.pathname === '/admin/biogas'
    // }
  ];

  return (
    <BaseLayout
      moduleName="Admin"
      moduleSubtitle="Platform Management"
      navigationItems={navigationItems}
      userInfo={user ? {
        name: user.name,
        role: user.department || 'User',
        avatar: undefined
      } : undefined}
    >
      <Suspense fallback={<LoadingFallback />}>
        <ModuleErrorBoundary>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/users" element={<UserManagement />} />
            {/* <Route path="/devices" element={<DeviceRegistry />} /> */}
            <Route path="/access-management" element={<UserAccessManagement />} />
            {/* <Route path="/biogas" element={<BiogasManagement />} /> */}
            <Route path="/profile" element={<Profile />} />
          </Routes>
        </ModuleErrorBoundary>
      </Suspense>
    </BaseLayout>
  );
};

export default AdminModule;
